import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Badge, Button, Space, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { getWorkOrderById, WorkOrderQueryResponse } from '@/api/workorder';
import dayjs from 'dayjs';

const statusMap: Record<string, { color: string; label: string }> = {
  pending: { color: 'orange', label: '待处理' },
  assigned: { color: 'blue', label: '已分配' },
  processing: { color: 'cyan', label: '处理中' },
  completed: { color: 'green', label: '已完成' },
  cancelled: { color: 'gray', label: '已取消' },
};

const priorityMap: Record<number, { color: string; label: string }> = {
  1: { color: 'red', label: '紧急' },
  2: { color: 'orange', label: '高' },
  3: { color: 'blue', label: '中' },
  4: { color: 'default', label: '低' },
};

const orderTypeMap: Record<string, string> = {
  repair: '维修',
  inspect: '巡检',
  install: '安装',
  other: '其他',
};

const WorkOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkOrderQueryResponse | null>(null);

  useEffect(() => {
    if (id) {
      setLoading(true);
      getWorkOrderById(id)
        .then((resp) => {
          if (resp.success) {
            setData(resp.result);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return <div style={{ textAlign: 'center', padding: '100px' }}>工单不存在</div>;
  }

  const statusInfo = statusMap[data.status] || { color: 'gray', label: data.status };
  const priorityInfo = priorityMap[data.priority] || { color: 'default', label: '未知' };

  return (
    <>
      <Space className="m20">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          返回
        </Button>
      </Space>

      <Card className="m20" title={`工单详情 - ${data.orderNo || ''}`}>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="工单编号">{data.orderNo || '-'}</Descriptions.Item>
          <Descriptions.Item label="工单类型">{orderTypeMap[data.orderType] || data.orderType || '-'}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Badge color={statusInfo.color} />
            <span className="ml5">{statusInfo.label}</span>
          </Descriptions.Item>
          <Descriptions.Item label="优先级">
            <Tag color={priorityInfo.color}>{priorityInfo.label}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="设备名称">{data.deviceName || '-'}</Descriptions.Item>
          <Descriptions.Item label="设备ID">{data.deviceId || '-'}</Descriptions.Item>
          <Descriptions.Item label="客户名称">{data.customerName || '-'}</Descriptions.Item>
          <Descriptions.Item label="服务商名称">{data.providerName || '-'}</Descriptions.Item>
          <Descriptions.Item label="联系人">{data.contactName || '-'}</Descriptions.Item>
          <Descriptions.Item label="联系电话">{data.contactPhone || '-'}</Descriptions.Item>
          <Descriptions.Item label="位置">{data.location || '-'}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{data.created ? dayjs(data.created).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
          <Descriptions.Item label="分配时间">{data.assignedTime ? dayjs(data.assignedTime).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
          <Descriptions.Item label="完成时间">{data.completedTime ? dayjs(data.completedTime).format('YYYY-MM-DD HH:mm:ss') : '-'}</Descriptions.Item>
          <Descriptions.Item label="描述" span={2}>{data.description || '-'}</Descriptions.Item>
          <Descriptions.Item label="备注" span={2}>{data.remark || '-'}</Descriptions.Item>
        </Descriptions>

        {data.images && (
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>图片：</div>
            <Space wrap>
              {data.images.split(',').map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`图片${idx + 1}`}
                  style={{ maxWidth: 200, maxHeight: 200, borderRadius: 4, border: '1px solid #eee' }}
                />
              ))}
            </Space>
          </div>
        )}
      </Card>
    </>
  );
};

export default WorkOrderDetail;
